import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Crown, Eye } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/shared/components/ui/command';
import { useAuth } from '@/shared/hooks/useAuth';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { usePermissionCheck } from '@/shared/hooks/usePermissionCheck';
import { authService } from '@/shared/lib/auth';
import { administrationApi } from '@/features/administration/api/administrationApi';
import { accountsApi } from '@/features/accounts/api/accountsApi';
import { getErrorMessage } from '@/shared/lib/error-handler';
import { toast } from '@/shared/hooks/use-toast';

interface AccountOption {
  id: string;
  nome: string;
  accountModel: string;
  ativo: boolean;
}

const PAGE_SIZE = 20;

// Seletor de visao (visoes estritas): platform admins alternam entre a visao
// Plataforma (administracao) e a visao de uma conta (operacao). A lista e
// paginada e buscada NO SERVIDOR — com centenas de contas, despejar tudo num
// dropdown era inavegavel (feedback de TST).
export function ViewSwitcher() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { isPlatformAdmin } = usePermissionCheck();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const actingId = user?.actingTenantId ?? null;
  const homeLabel = isPlatformAdmin() ? t('viewSwitcher.platformLabel') : t('viewSwitcher.myAccountLabel');
  const currentLabel = actingId ? (user?.actingTenantName ?? t('viewSwitcher.accountFallback')) : homeLabel;

  // Contas-pai (nao platform admin) EM visao assumida: accountsApi.getChildren
  // e escopado por tenantContext.TenantId, que aqui ja aponta para a conta
  // ASSUMIDA (a filha), nao para o pai real. Buscar contas nesse estado listaria
  // as NETAS (a politica de acting-view proibe assumi-las -> erro ao clicar) ou
  // devolveria 403 se a filha for Standalone. Decisao do dono do produto: em
  // visao assumida o seletor so mostra o item de retorno ("Minha conta"); para
  // ir a outra filha o usuario volta para casa e entra de novo. O platform
  // admin do Root usa administrationApi.getTenants, que nao e escopado pelo
  // tenant atuante — continua listando normalmente e NAO passa por este gate.
  const accountsListLocked = !isPlatformAdmin() && !!actingId;

  useEffect(() => {
    if (!open || accountsListLocked) return;
    let cancelled = false;
    setLoading(true);
    const fetchAccounts = isPlatformAdmin()
      ? administrationApi.getTenants({
          search: debouncedSearch || undefined,
          page: 1,
          pageSize: PAGE_SIZE,
        })
      // Conta-pai enxerga apenas as proprias filhas diretas — mesmo endpoint
      // da tela de Contas, mesmo gate de isolamento no backend.
      : accountsApi.getChildren({
          search: debouncedSearch || undefined,
          page: 1,
          pageSize: PAGE_SIZE,
        });

    fetchAccounts
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.items.map((item) => ({
          id: item.id, nome: item.nome, accountModel: item.accountModel, ativo: item.ativo,
        })));
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        toast({ title: t('viewSwitcher.listError'), description: getErrorMessage(e), variant: 'destructive' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, debouncedSearch, accountsListLocked, t]);

  if (!user?.canAssumeView) return null;

  const switchTo = async (targetId: string | null) => {
    if (switching) return;
    if (targetId === actingId) return;
    setSwitching(true);
    try {
      const api = isPlatformAdmin() ? administrationApi : accountsApi;
      const auth = targetId ? await api.assumeView(targetId) : await api.releaseView();
      authService.completeLogin(auth);
      window.location.assign('/');
    } catch (e) {
      setSwitching(false);
      toast({ title: t('viewSwitcher.switchError'), description: getErrorMessage(e), variant: 'destructive' });
    }
  };

  const visible = accounts.filter((a) => a.ativo && a.accountModel !== 'Root');

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          // Limpa busca E resultados: reabrir mostrava a lista da busca
          // anterior sob um input vazio (achado da revisao).
          setSearch('');
          setAccounts([]);
          setTotal(0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between gap-2 bg-sidebar-accent"
          disabled={switching}
          data-testid="view-switcher"
        >
          <span className="flex items-center gap-2 truncate">
            {actingId ? <Eye className="h-4 w-4 shrink-0 text-primary" /> : <Crown className="h-4 w-4 shrink-0 text-primary" />}
            <span className="truncate text-sm">{t('viewSwitcher.currentView', { label: currentLabel })}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        {/* Busca server-side: o filtro do cmdk fica desligado */}
        <Command shouldFilter={false}>
          {/* Sem lista de contas para buscar em visao assumida (nao-admin):
              omitir o input evita um popover em branco enquanto o usuario digita. */}
          {!accountsListLocked && (
            <CommandInput
              placeholder={t('viewSwitcher.searchPlaceholder')}
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList>
            {/* Escondido durante a busca: o cmdk re-destaca o PRIMEIRO item a
                cada tecla — Enter apos digitar cairia em Plataforma e
                derrubaria o admin da visao assumida (achado da revisao). */}
            {!search && (
              <>
                <CommandGroup>
                  <CommandItem onSelect={() => switchTo(null)}>
                    <Crown className="mr-2 h-4 w-4 shrink-0" />
                    {isPlatformAdmin() ? t('viewSwitcher.platformOption') : t('viewSwitcher.myAccountOption', { name: user?.name ?? '' })}
                    {!actingId && <Check className="ml-auto h-4 w-4 shrink-0" />}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {/* Em visao assumida (nao platform admin) o seletor so mostra o
                retorno acima — accountsListLocked ja impede a busca; aqui so
                evitamos deixar a secao "Contas" vazia ou com mensagem enganosa. */}
            {!accountsListLocked && (
              <CommandGroup heading={t('nav.accounts')}>
                {loading && visible.length === 0 && (
                  <CommandItem disabled>{t('viewSwitcher.loadingAccounts')}</CommandItem>
                )}
                {visible.map((a) => (
                  <CommandItem key={a.id} value={a.id} onSelect={() => switchTo(a.id)}>
                    <Eye className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">{a.nome}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{a.accountModel}</span>
                    {actingId === a.id && <Check className="ml-auto h-4 w-4 shrink-0" />}
                  </CommandItem>
                ))}
                {/* Div simples: CommandEmpty nunca renderiza com shouldFilter=false */}
                {!loading && visible.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t('viewSwitcher.noAccountsFound')}
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
          {!accountsListLocked && total > visible.length && (
            <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
              {t('viewSwitcher.showingCount', { shown: visible.length, total })}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
